import { FormEvent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '@components/Button';
import { supabase } from '@lib/supabase';

export default function ResetPassword() {
  const navigate = useNavigate();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isVerifyingLink, setIsVerifyingLink] = useState(true);
  const [canReset, setCanReset] = useState(false);

  const calculatePasswordStrength = (pwd: string) => {
    let score = 0;
    if (pwd.length >= 8) score++;
    if (pwd.length >= 12) score++;
    if (/[a-z]/.test(pwd) && /[A-Z]/.test(pwd)) score++;
    if (/\d/.test(pwd)) score++;
    if (/[^a-zA-Z0-9]/.test(pwd)) score++;

    const labels: { label: string; color: string }[] = [
      { label: 'Too weak', color: 'bg-red-500' },
      { label: 'Weak', color: 'bg-orange-500' },
      { label: 'Fair', color: 'bg-yellow-500' },
      { label: 'Good', color: 'bg-lime-500' },
      { label: 'Strong', color: 'bg-green-500' },
      { label: 'Very strong', color: 'bg-emerald-600' },
    ];

    const strength = labels[Math.min(score, labels.length - 1)]!;
    return { score, label: strength.label, color: strength.color };
  };

  const passwordRequirements = (pwd: string) => {
    return {
      length8: pwd.length >= 8,
      uppercase: /[A-Z]/.test(pwd),
      lowercase: /[a-z]/.test(pwd),
      number: /\d/.test(pwd),
      special: /[^a-zA-Z0-9]/.test(pwd),
    };
  };

  const passwordStrength = newPassword
    ? calculatePasswordStrength(newPassword)
    : null;
  const reqs = passwordRequirements(newPassword);

  useEffect(() => {
    let isMounted = true;

    const ensureRecoverySession = async () => {
      setIsVerifyingLink(true);
      try {
        let {
          data: { session },
          error: sessionError,
        } = await supabase.auth.getSession();

        if (sessionError) {
          throw sessionError;
        }

        const hashParams = new URLSearchParams(window.location.hash.slice(1));
        const searchParams = new URLSearchParams(
          window.location.search.slice(1)
        );
        const hasRecoveryParams =
          hashParams.has('access_token') ||
          hashParams.has('refresh_token') ||
          hashParams.has('code') ||
          searchParams.has('code');

        if (!session && hasRecoveryParams) {
          try {
            const exchange =
              typeof supabase.auth.exchangeCodeForSession === 'function'
                ? supabase.auth.exchangeCodeForSession
                : null;
            if (exchange) {
              // Ensure the temporary recovery session is established from the URL params.
              await exchange(window.location.href);
              ({
                data: { session },
                error: sessionError,
              } = await supabase.auth.getSession());
              if (sessionError) {
                throw sessionError;
              }
            }
          } catch (exchangeError) {
            console.error('Failed to exchange recovery code', exchangeError);
          }
        }

        if (!session) {
          if (isMounted) {
            setError(
              'This reset link is invalid or has expired. Please request a new reset email.'
            );
            setCanReset(false);
          }
          return;
        }

        if (isMounted) {
          setCanReset(true);
          setError(null);
        }
      } catch (err: any) {
        if (!isMounted) return;
        setError(
          err?.message ||
            'We were unable to validate your reset link. Please request a new one.'
        );
        setCanReset(false);
      } finally {
        if (isMounted) {
          setIsVerifyingLink(false);
        }
      }
    };

    ensureRecoverySession();

    return () => {
      isMounted = false;
    };
  }, []);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (loading || !canReset) return;

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (updateError) {
        throw updateError;
      }

      setSuccessMessage(
        'Your password has been updated successfully. You can now sign in with your new credentials.'
      );
      setNewPassword('');
      setConfirmPassword('');

      // Drop the one-time recovery session so the next visit requires the new password.
      void supabase.auth.signOut().catch((signOutError) => {
        console.error('Sign out after password reset failed', signOutError);
      });
    } catch (err: any) {
      setError(err?.message || 'Failed to update password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToSignIn = async () => {
    try {
      await supabase.auth.signOut();
    } catch (signOutError) {
      console.error('Sign out before redirect failed', signOutError);
    } finally {
      navigate('/signin', { replace: true });
    }
  };

  return (
    <div className="animate-fade-in flex min-h-screen items-center justify-center bg-gradient-to-br from-blue-100 via-teal-50 to-cyan-100 px-4 py-12">
      <div className="w-full max-w-md">
        <div className="animate-slide-up rounded-2xl bg-white p-8 shadow-xl">
          <div className="mb-6 text-center">
            <h1 className="mb-2 text-3xl font-bold text-gray-900">
              Reset Password
            </h1>
            <p className="text-sm text-gray-600">
              Choose a new password to secure your account.
            </p>
          </div>

          {isVerifyingLink && (
            <div className="mb-4 rounded-lg bg-indigo-50 p-4 text-sm text-indigo-800">
              Validating your reset link...
            </div>
          )}

          {error && (
            <div className="mb-4 rounded-lg bg-red-50 p-4 text-sm text-red-800">
              {error}
            </div>
          )}

          {successMessage ? (
            <div className="space-y-6">
              <div className="rounded-lg bg-emerald-50 p-4 text-sm text-emerald-800">
                {successMessage}
              </div>
              <Button
                type="button"
                variant="primary"
                size="lg"
                className="begin-breathing-button w-full"
                onClick={handleBackToSignIn}
              >
                Go to Sign In
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label
                  htmlFor="new-password"
                  className="mb-2 block text-sm font-medium text-gray-700"
                >
                  New Password
                </label>
                <input
                  id="new-password"
                  type="password"
                  value={newPassword}
                  onChange={(event) => setNewPassword(event.target.value)}
                  placeholder="Enter your new password"
                  className="w-full rounded-lg border border-gray-300 px-4 py-3 transition-all focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                  disabled={loading || !canReset}
                  minLength={8}
                  required
                />
                {newPassword && passwordStrength && (
                  <div className="animate-fade-in mt-2">
                    <div className="mb-2 flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">
                        Password Strength
                      </span>
                      <span className="text-sm font-semibold text-gray-800">
                        {passwordStrength.label}
                      </span>
                    </div>

                    <div className="mb-3 h-2 overflow-hidden rounded-full bg-gray-200">
                      <div
                        className={`h-full ${passwordStrength.color} transition-all duration-300`}
                        style={{
                          width: `${(passwordStrength.score / 5) * 100}%`,
                        }}
                      />
                    </div>

                    <div className="mb-3 text-sm text-gray-700">
                      <div className="mb-2">Requirements:</div>
                      <ul className="space-y-2">
                        <li className="flex items-start gap-3">
                          <span
                            className={`mt-0.5 flex h-5 w-5 items-center justify-center rounded-full ${reqs.length8 ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'}`}
                          >
                            {reqs.length8 ? (
                              <svg
                                className="h-3 w-3"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M16.707 5.293a1 1 0 00-1.414-1.414L8 11.172 4.707 7.879a1 1 0 10-1.414 1.414l4 4a1 1 0 001.414 0l8-8z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            ) : (
                              <svg
                                className="h-3 w-3"
                                viewBox="0 0 20 20"
                                fill="none"
                                stroke="currentColor"
                              >
                                <circle
                                  cx="10"
                                  cy="10"
                                  r="9"
                                  strokeWidth="1.5"
                                />
                              </svg>
                            )}
                          </span>
                          <span
                            className={`text-sm ${reqs.length8 ? 'text-green-600' : 'text-gray-600'}`}
                          >
                            At least 8 characters
                          </span>
                        </li>

                        <li className="flex items-start gap-3">
                          <span
                            className={`mt-0.5 flex h-5 w-5 items-center justify-center rounded-full ${reqs.uppercase ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'}`}
                          >
                            {reqs.uppercase ? (
                              <svg
                                className="h-3 w-3"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M16.707 5.293a1 1 0 00-1.414-1.414L8 11.172 4.707 7.879a1 1 0 10-1.414 1.414l4 4a1 1 0 001.414 0l8-8z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            ) : (
                              <svg
                                className="h-3 w-3"
                                viewBox="0 0 20 20"
                                fill="none"
                                stroke="currentColor"
                              >
                                <circle
                                  cx="10"
                                  cy="10"
                                  r="9"
                                  strokeWidth="1.5"
                                />
                              </svg>
                            )}
                          </span>
                          <span
                            className={`text-sm ${reqs.uppercase ? 'text-green-600' : 'text-gray-600'}`}
                          >
                            One uppercase letter (A-Z)
                          </span>
                        </li>

                        <li className="flex items-start gap-3">
                          <span
                            className={`mt-0.5 flex h-5 w-5 items-center justify-center rounded-full ${reqs.lowercase ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'}`}
                          >
                            {reqs.lowercase ? (
                              <svg
                                className="h-3 w-3"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M16.707 5.293a1 1 0 00-1.414-1.414L8 11.172 4.707 7.879a1 1 0 10-1.414 1.414l4 4a1 1 0 001.414 0l8-8z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            ) : (
                              <svg
                                className="h-3 w-3"
                                viewBox="0 0 20 20"
                                fill="none"
                                stroke="currentColor"
                              >
                                <circle
                                  cx="10"
                                  cy="10"
                                  r="9"
                                  strokeWidth="1.5"
                                />
                              </svg>
                            )}
                          </span>
                          <span
                            className={`text-sm ${reqs.lowercase ? 'text-green-600' : 'text-gray-600'}`}
                          >
                            One lowercase letter (a-z)
                          </span>
                        </li>

                        <li className="flex items-start gap-3">
                          <span
                            className={`mt-0.5 flex h-5 w-5 items-center justify-center rounded-full ${reqs.number ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'}`}
                          >
                            {reqs.number ? (
                              <svg
                                className="h-3 w-3"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M16.707 5.293a1 1 0 00-1.414-1.414L8 11.172 4.707 7.879a1 1 0 10-1.414 1.414l4 4a1 1 0 001.414 0l8-8z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            ) : (
                              <svg
                                className="h-3 w-3"
                                viewBox="0 0 20 20"
                                fill="none"
                                stroke="currentColor"
                              >
                                <circle
                                  cx="10"
                                  cy="10"
                                  r="9"
                                  strokeWidth="1.5"
                                />
                              </svg>
                            )}
                          </span>
                          <span
                            className={`text-sm ${reqs.number ? 'text-green-600' : 'text-gray-600'}`}
                          >
                            One number (0-9)
                          </span>
                        </li>

                        <li className="flex items-start gap-3">
                          <span
                            className={`mt-0.5 flex h-5 w-5 items-center justify-center rounded-full ${reqs.special ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'}`}
                          >
                            {reqs.special ? (
                              <svg
                                className="h-3 w-3"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M16.707 5.293a1 1 0 00-1.414-1.414L8 11.172 4.707 7.879a1 1 0 10-1.414 1.414l4 4a1 1 0 001.414 0l8-8z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            ) : (
                              <svg
                                className="h-3 w-3"
                                viewBox="0 0 20 20"
                                fill="none"
                                stroke="currentColor"
                              >
                                <circle
                                  cx="10"
                                  cy="10"
                                  r="9"
                                  strokeWidth="1.5"
                                />
                              </svg>
                            )}
                          </span>
                          <span
                            className={`text-sm ${reqs.special ? 'text-green-600' : 'text-gray-600'}`}
                          >
                            One special character (!@#$%^&*...)
                          </span>
                        </li>
                      </ul>
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label
                  htmlFor="confirm-password"
                  className="mb-2 block text-sm font-medium text-gray-700"
                >
                  Confirm New Password
                </label>
                <input
                  id="confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(event) => setConfirmPassword(event.target.value)}
                  placeholder="Re-enter your new password"
                  className="w-full rounded-lg border border-gray-300 px-4 py-3 transition-all focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                  disabled={loading || !canReset}
                  minLength={8}
                  required
                />
              </div>

              <Button
                type="submit"
                variant="primary"
                size="lg"
                className="begin-breathing-button w-full"
                disabled={loading || !canReset || isVerifyingLink}
              >
                {loading ? 'Updating password...' : 'Update Password'}
              </Button>

              <button
                type="button"
                onClick={() => navigate('/signin')}
                className="w-full text-sm text-gray-600 transition-colors hover:text-gray-900 hover:underline"
                disabled={loading}
              >
                Back to Sign In
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
